<template>
  <PageTemplate>

    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="d-flex flex-column flex-md-row justify-content-md-between align-items-md-center mb-4">
        <h6 class="element-header mb-3 mb-md-0 text-center text-md-start">
          {{ $t('course.general_title') }}
        </h6>

        <div class="row g-2 justify-content-center justify-content-md-end">
          <div class="col-12 col-sm-auto">
            <CreateButton
                class="w-100"
                :label="$t('course.private_coaching.module.create_random_goal')"
                :to="{ name: 'coursePrivateCoachingRandomGoalList' }"/>
          </div>

          <div class="col-12 col-sm-auto mt-3 mt-sm-0">
            <CreateButton
                class="w-100"
                :label="$t('course.private_coaching.module.create')"
                :to="{ name: 'coursePrivateCoachingModuleForm' }"/>
          </div>
        </div>
      </div>

      <div class="element-box">
        <h5 class="form-header mb-4">
          {{ $t('course.private_coaching.module.list') }}
        </h5>

        <Search
            :filters="filters"
            :loading="isSearching"
            :operationTypeDropdown="operationTypeFiltered"
            @search="getModules"/>

        <PerPage :values="[10, 25, 50, 100]" @update:perPage="getModules"/>

        <EmptyState
            v-if="!modules.data?.length"
            icon="fa-layer-group"
            title="Keine Module gefunden"
            subtitle="Es wurden keine Module gefunden, die Ihren Suchkriterien entsprechen."/>

        <div v-else class="table-responsive overflow-auto pt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>Name</th>
              <th>Maßnahmenart</th>
              <th class="text-end">Kosten</th>
              <th class="text-end">Einheiten</th>
              <th class="text-end">Bestellnummer</th>
              <th style=" text-align: right; padding-right: 30px; min-width: 150px;">{{ $t('general.actions') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(module) in modules.data" :key="module.id">
              <td>{{ module?.name }}</td>
              <td>{{ module?.operationTypeTranslated }}</td>
              <td class="text-end">{{ module?.cost }}</td>
              <td class="text-end">{{ module?.units }}</td>
              <td class="text-end">{{ module?.orderNumber }}</td>
              <td class="text-end">
                <button class="btn btn-primary"
                        @click="router.push({name: 'coursePrivateCoachingModuleForm', params: {id: module?.id} })">
                  <i class="os-icon os-icon-edit"></i>
                </button>
                <button class="btn btn-danger ms-2"
                        @click="deleteModal(module?.id)">
                  <i class="os-icon os-icon-trash"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="modules?.pagination?.total_pages > 1"
            :totalPages="modules?.pagination?.total_pages"
            :currentPage="modules?.pagination?.current_page"
            :totalItems="modules?.pagination?.total_items"
            :perPage="modules?.pagination?.items_per_page"
            @pagechanged="getModules"/>
      </div>
    </div>

    <ModalDelete
        @update:modelValue="updateModal"
        :modelValue="showModal"
        :header="t('general.delete_item_header')"
        :onDelete="deleteModule">
    </ModalDelete>

  </PageTemplate>
</template>


<script setup>
import coursePrivateCoachingModuleService from '@/api/Course/PrivateCoachingModule';
import CreateButton from '@/components/CreateButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import Loader from '@/components/Loader.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import router from '@/router';
import {piniaEnumsStore} from '@/store/piniaEnumsStore';
import Search from '@/views/App/Course/PrivateCoachingModule/Search.vue';
import {computed, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';

const {t} = useI18n();

const showModal = ref(false);
const moduleId = ref(0);
const isSearching = ref(false);
const isLoaded = ref(false);
const modules = ref([]);
const filters = ref({
  name: '',
  operationType: '',
});

const piniaEnumsState = piniaEnumsStore();
const operationTypeFiltered = computed(() => {
  return piniaEnumsState.enums.privateCoachingOperationTypes.filter(status =>
      status.value === 'modular_integration_coaching' || status.value === 'go_work_mobile'
  );
});

async function getModules(page = 1, perPage, passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }

  const perPageRes = modules.value?.pagination?.items_per_page;

  try {
    const {data} = await coursePrivateCoachingModuleService.getModules({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : filters.value),
    });

    modules.value = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

async function deleteModule() {
  try {
    await coursePrivateCoachingModuleService.deleteModule(moduleId.value);

    filters.value.name = '';
    filters.value.operationType = '';
    showModal.value = false;

    await getModules();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

function updateModal(value) {
  CloseToast();
  showModal.value = value;
}

function deleteModal(id) {
  CloseToast();
  moduleId.value = id;
  showModal.value = true;
}

onMounted(async () => {
  await Promise.all([
    getModules(),
  ]);

  isLoaded.value = true;
});
</script>

<style scoped>

</style>